/**
 * Find the shortest route (with least turns) between two points on the path network.
 *
 * @param {Array} roundedPaths - Array of path objects with .allPoints and .id
 * @param {Array} startCoord - [lat, lng] of start point (will snap to nearest path point)
 * @param {Array} endCoord - [lat, lng] of end point (will snap to nearest path point)
 * @returns {{ path: Array, startPoint: Array, endPoint: Array, direction: string|null } | null}
 */
export function findPath(roundedPaths, startCoord, endCoord) {
	if (!roundedPaths || !roundedPaths.length || !startCoord || !endCoord) return null;

	const start = [Math.round(startCoord[0]), Math.round(startCoord[1])];
	const end = [Math.round(endCoord[0]), Math.round(endCoord[1])];

	// Find closest path point to start
	let closestPathToStart = null;
	let closestDistanceToStart = null;
	let closestPointToStart = null;

	roundedPaths.forEach((path) => {
		path.allPoints.forEach((point) => {
			const distance = Math.sqrt(Math.pow(point[0] - start[0], 2) + Math.pow(point[1] - start[1], 2));
			if (!closestDistanceToStart || distance < closestDistanceToStart) {
				closestDistanceToStart = distance;
				closestPathToStart = path;
				closestPointToStart = point;
			}
		});
	});

	// Find closest path point to end
	let closestDistanceToEnd = null;
	let closestPointToEnd = null;

	roundedPaths.forEach((path) => {
		path.allPoints.forEach((point) => {
			const distance = Math.sqrt(Math.pow(point[0] - end[0], 2) + Math.pow(point[1] - end[1], 2));
			if (!closestDistanceToEnd || distance < closestDistanceToEnd) {
				closestDistanceToEnd = distance;
				closestPointToEnd = point;
			}
		});
	});

	if (!closestPointToStart || !closestPointToEnd) return null;

	// Find all unique intersections between paths
	const intersections = [];
	roundedPaths.forEach((path1) => {
		roundedPaths.forEach((path2) => {
			if (path1.id !== path2.id) {
				path1.allPoints.forEach((point1) => {
					path2.allPoints.forEach((point2) => {
						if (point1[0] === point2[0] && point1[1] === point2[1]) {
							intersections.push(point1);
						}
					});
				});
			}
		});
	});

	const uniqueIntersections = [];
	intersections.forEach((intersection) => {
		const hasIntersection = uniqueIntersections.find((uniqueIntersection) => {
			return uniqueIntersection[0] === intersection[0] && uniqueIntersection[1] === intersection[1];
		});
		if (!hasIntersection) uniqueIntersections.push(intersection);
	});

	// DFS to find route with least turns
	let leastTurnsRoutes = [];
	let combos = 0;

	const traverseRoute = (currentPoint, currentPath, prevRoutes, usedPaths) => {
		if (combos > 1000) return;
		const points = [...prevRoutes, currentPoint];

		if (leastTurnsRoutes.length && points.length === leastTurnsRoutes[0].length) {
			return;
		}

		const hasEndPoint = currentPath?.allPoints.find((point) => {
			return point[0] === closestPointToEnd[0] && point[1] === closestPointToEnd[1];
		});

		if (hasEndPoint) {
			points.push(closestPointToEnd);

			if (leastTurnsRoutes.length === 0) {
				combos++;
				leastTurnsRoutes.push(points);
				return;
			}

			if (points.length === leastTurnsRoutes[0].length) {
				combos++;
				leastTurnsRoutes.push(points);
				return;
			}

			if (points.length < leastTurnsRoutes[0].length) {
				combos++;
				leastTurnsRoutes = [points];
				return;
			}

			return;
		}

		const currentIntersections = [];
		uniqueIntersections.forEach((intersection) => {
			currentPath.allPoints.forEach((point) => {
				if (point[0] === intersection[0] && point[1] === intersection[1]) {
					currentIntersections.push(intersection);
				}
			});
		});

		const newRoundedPaths = roundedPaths.filter((path) => {
			return !usedPaths.includes(path.id);
		});

		newRoundedPaths.forEach((path) => {
			if (!usedPaths.includes(path.id)) {
				currentIntersections.forEach((intersection) => {
					path.allPoints.forEach((point) => {
						if (point[0] === intersection[0] && point[1] === intersection[1]) {
							traverseRoute(intersection, path, points, [...usedPaths, path.id]);
						}
					});
				});
			}
		});
	};

	traverseRoute(closestPointToStart, closestPathToStart, [], [closestPathToStart?.id]);

	// Find shortest route among least-turns routes
	let shortestRoute = null;
	let shortestRouteSteps = null;

	leastTurnsRoutes.forEach((route) => {
		let steps = 0;
		route.reduce((prevPoint, currentPoint) => {
			const diff1 = prevPoint[0] - currentPoint[0];
			const diff2 = prevPoint[1] - currentPoint[1];
			const sumDiff = Math.abs(diff1) + Math.abs(diff2);
			steps += sumDiff;
			return currentPoint;
		});

		if (!shortestRoute || steps < shortestRouteSteps) {
			shortestRoute = route;
			shortestRouteSteps = steps;
		}

		if (steps === shortestRouteSteps) {
			if (route.length < shortestRoute.length) {
				shortestRoute = route;
				shortestRouteSteps = steps;
			}
		}
	});

	if (!shortestRoute) return null;

	// Determine direction from last two points
	const lastPoint = shortestRoute[shortestRoute.length - 1];
	const secondLastPoint = shortestRoute[shortestRoute.length - 2];

	let direction = null;
	const dLat = (lastPoint?.[0] || 0) - (secondLastPoint?.[0] || 0);
	const dLng = (lastPoint?.[1] || 0) - (secondLastPoint?.[1] || 0);

	// Use the dominant axis to pick direction (works for both axis-aligned and diagonal)
	if (Math.abs(dLng) >= Math.abs(dLat)) {
		direction = dLng > 0 ? 'right' : 'left';
	} else {
		direction = dLat > 0 ? 'up' : 'down';
	}

	return {
		path: shortestRoute,
		startPoint: closestPointToStart,
		endPoint: closestPointToEnd,
		direction,
	};
}

/**
 * Find the closest point on any path to a given coordinate.
 */
export function findClosestPathPoint(roundedPaths, coord) {
	const target = [Math.round(coord[0]), Math.round(coord[1])];
	let closestDistance = null;
	let closestPoint = null;

	roundedPaths.forEach((path) => {
		path.allPoints.forEach((point) => {
			const distance = Math.sqrt(Math.pow(point[0] - target[0], 2) + Math.pow(point[1] - target[1], 2));
			if (!closestDistance || distance < closestDistance) {
				closestDistance = distance;
				closestPoint = point;
			}
		});
	});

	return closestPoint;
}
